import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { IBAN_MAX_LENGTH } from '~/constants/common.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import {
  GUARANTEE_SECURITY_TYPE_UI_OPTIONS,
  PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS,
} from '../../../rootSection/constants.ts';
import {
  GuaranteesAccountTargetField,
  GuaranteesPrincipalConstituentDocumentsChangesOption,
  type TGuaranteesAccountOption,
  type TGuaranteesRootSectionFormValues,
} from '../../../rootSection/types.ts';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';
import FlowSection from '../shared/FlowSection.tsx';
import AccountTable from './AccountTable.tsx';

interface IAccountSectionProps {
  accountOptions: TGuaranteesAccountOption[];
  principalDebitAccountOptions: TGuaranteesAccountOption[];
  securityCurrencyOptions: TAutocompleteOption[];
  isLoadingAccounts: boolean;
  isLoadingPrincipalDebitAccounts: boolean;
  isLoadingSecurityCurrencies: boolean;
  isLoadingCurrencies: boolean;
  isSavedGuaranteeTypeSelected: boolean;
  isSavingAccount: boolean;
  isSecurityTypeSelected: boolean;
  isCounterpartyAccount: boolean;
  isCounterpartyPrincipalDebitAccount: boolean;
  hasChanges?: boolean;
  lockedUntilSavedHint: string;
  manualAccountFieldName: GuaranteesAccountTargetField | null;
  selectedAccount: TGuaranteesAccountOption | null;
  onAccountSelect: (option: TAutocompleteOption | TAutocompleteOption[] | null) => void;
  onCounterpartyAccountChange: (isCounterpartyAccount: boolean) => void;
  onManualAccountIbanChange: () => void;
  onPrincipalDebitAccountSelect: (
    option: TAutocompleteOption | TAutocompleteOption[] | null
  ) => void;
  onCounterpartyPrincipalDebitAccountChange: (isCounterpartyAccount: boolean) => void;
  onManualPrincipalDebitAccountIbanChange: () => void;
  onSave: () => Promise<void>;
}

const AccountSection: React.FC<IAccountSectionProps> = ({
  accountOptions,
  principalDebitAccountOptions,
  securityCurrencyOptions,
  isLoadingAccounts,
  isLoadingPrincipalDebitAccounts,
  isLoadingSecurityCurrencies,
  isLoadingCurrencies,
  isSavedGuaranteeTypeSelected,
  isSavingAccount,
  isSecurityTypeSelected,
  isCounterpartyAccount,
  isCounterpartyPrincipalDebitAccount,
  hasChanges = false,
  lockedUntilSavedHint,
  manualAccountFieldName,
  selectedAccount,
  onAccountSelect,
  onCounterpartyAccountChange,
  onManualAccountIbanChange,
  onPrincipalDebitAccountSelect,
  onCounterpartyPrincipalDebitAccountChange,
  onManualPrincipalDebitAccountIbanChange,
  onSave,
}) => {
  const { isReadOnly, requireChangesToEnableActions } =
    useGuaranteesRootApplicationSectionContext();
  const { control } = useFormContext<TGuaranteesRootSectionFormValues>();
  const pledgorHasChangesInConstituentDocuments = useWatch({
    control,
    name: 'pledgorHasChangesInConstituentDocuments',
  });
  const shouldShowPledgorIdentifyCode =
    pledgorHasChangesInConstituentDocuments?.value ===
    GuaranteesPrincipalConstituentDocumentsChangesOption.YES;
  const isAccountTableDisabled =
    !isSavedGuaranteeTypeSelected ||
    !isSecurityTypeSelected ||
    isReadOnly ||
    isSavingAccount ||
    isLoadingAccounts;
  const isSaveButtonDisabled =
    !isSavedGuaranteeTypeSelected ||
    isSavingAccount ||
    isReadOnly ||
    (requireChangesToEnableActions && !hasChanges);
  const isAccountModeDisabled =
    !isSavedGuaranteeTypeSelected || !isSecurityTypeSelected || isReadOnly || isSavingAccount;
  const areSecurityDetailsDisabled = !isSavedGuaranteeTypeSelected || isReadOnly || isSavingAccount;
  const isPrincipalDebitAccountDisabled =
    areSecurityDetailsDisabled || isLoadingPrincipalDebitAccounts;
  const shouldShowSecurityAccount = !!manualAccountFieldName;

  return (
    <FlowSection
      step={guaranteesClientText.accountSectionStepLabel}
      title={guaranteesClientText.accountSectionTitle}
      description={guaranteesClientText.accountSectionDescription}
      disabled={!isSavedGuaranteeTypeSelected}
    >
      <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
        <FormInput
          name="securityAmount"
          label={guaranteesClientText.securityAmountLabel}
          type="number"
          disabled={isReadOnly}
          sx={guaranteesClientPageStyles.flowField}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
            htmlInput: {
              min: 0,
              step: '0.01',
            },
          }}
        />

        <FormAutocomplete
          name="securityCurrencies"
          label="Валюти забезпечення"
          placeholder="Оберіть валюти"
          options={securityCurrencyOptions}
          multiple
          disabled={
            areSecurityDetailsDisabled ||
            !shouldShowSecurityAccount ||
            isLoadingCurrencies ||
            isLoadingSecurityCurrencies
          }
          sx={guaranteesClientPageStyles.flowField}
        />

        <FormInput
          name="requiredSecurityAmount"
          label="Розмір необхідного забезпечення"
          disabled={areSecurityDetailsDisabled}
          sx={guaranteesClientPageStyles.flowField}
        />

        <FormInput
          name="pledgorName"
          label="Найменування заставодавця"
          disabled={areSecurityDetailsDisabled}
          sx={guaranteesClientPageStyles.flowField}
        />

        <FormAutocomplete
          name="pledgorHasChangesInConstituentDocuments"
          label={guaranteesClientText.pledgorHasChangesInConstituentDocumentsLabel}
          options={PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS}
          disabled={areSecurityDetailsDisabled}
          notNullable
          sx={guaranteesClientPageStyles.flowField}
        />

        {shouldShowPledgorIdentifyCode && (
          <FormInput
            name="pledgorIdentifyCode"
            label={guaranteesClientText.pledgorIdentifyCodeLabel}
            disabled={areSecurityDetailsDisabled}
            sx={guaranteesClientPageStyles.flowField}
          />
        )}
      </Box>

      {isSavedGuaranteeTypeSelected && (
        <Box sx={guaranteesClientPageStyles.flowAccountSubsection}>
          <Typography sx={guaranteesClientPageStyles.flowAccountSubsectionTitle}>
            {guaranteesClientText.principalDebitAccountSectionLabel}
          </Typography>

          <Box sx={guaranteesClientPageStyles.flowAccountInlineRow}>
            <FormCheckbox
              name="isCounterpartyPrincipalDebitAccount"
              label={guaranteesClientText.counterpartyAccountLabel}
              disabled={areSecurityDetailsDisabled}
              onChange={onCounterpartyPrincipalDebitAccountChange}
            />

            <Box sx={guaranteesClientPageStyles.flowAccountInlineField}>
              {isCounterpartyPrincipalDebitAccount ? (
                <FormAutocomplete
                  name="principalDebitAccount"
                  placeholder="Оберіть рахунок"
                  options={principalDebitAccountOptions}
                  disabled={isPrincipalDebitAccountDisabled}
                  loading={isLoadingPrincipalDebitAccounts}
                  onChange={onPrincipalDebitAccountSelect}
                  sx={guaranteesClientPageStyles.flowManualAccountField}
                />
              ) : (
                <FormInput
                  name="principalDebitAccountIban"
                  placeholder={guaranteesClientText.manualAccountIbanPlaceholder}
                  disabled={areSecurityDetailsDisabled}
                  onChange={onManualPrincipalDebitAccountIbanChange}
                  sx={guaranteesClientPageStyles.flowManualAccountField}
                  inputProps={{
                    maxLength: IBAN_MAX_LENGTH,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={guaranteesClientPageStyles.flowAccountSubsection}>
        <Typography sx={guaranteesClientPageStyles.flowAccountSubsectionTitle}>
          {guaranteesClientText.securityAccountDetailsLabel}
        </Typography>

        <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
          <FormAutocomplete
            name="securityType"
            label={guaranteesClientText.securityTypeLabel}
            placeholder={guaranteesClientText.securityTypePlaceholder}
            options={GUARANTEE_SECURITY_TYPE_UI_OPTIONS}
            disabled={isReadOnly}
            sx={guaranteesClientPageStyles.flowField}
          />
        </Box>

        {isSavedGuaranteeTypeSelected && isSecurityTypeSelected && shouldShowSecurityAccount && (
          <Box>
            <FormCheckbox
              name="isCounterpartyAccount"
              label={guaranteesClientText.counterpartyAccountLabel}
              disabled={isAccountModeDisabled}
              onChange={onCounterpartyAccountChange}
            />
          </Box>
        )}

        {shouldShowSecurityAccount &&
          (isCounterpartyAccount ? (
            <AccountTable
              accountOptions={accountOptions}
              isDisabled={isAccountTableDisabled}
              isLoading={isLoadingAccounts}
              selectedAccount={selectedAccount}
              onAccountSelect={(option: TGuaranteesAccountOption) => onAccountSelect(option)}
            />
          ) : (
            <Box sx={guaranteesClientPageStyles.flowManualAccountFieldWrap}>
              <FormInput
                name={manualAccountFieldName}
                label={guaranteesClientText.manualAccountIbanLabel}
                placeholder={guaranteesClientText.manualAccountIbanPlaceholder}
                disabled={isAccountTableDisabled}
                onChange={onManualAccountIbanChange}
                sx={guaranteesClientPageStyles.flowManualAccountField}
                inputProps={{
                  maxLength: IBAN_MAX_LENGTH,
                }}
              />
            </Box>
          ))}
      </Box>

      {!isSavedGuaranteeTypeSelected && (
        <Typography sx={guaranteesClientPageStyles.flowLockHint}>{lockedUntilSavedHint}</Typography>
      )}

      {isSavedGuaranteeTypeSelected && !isSecurityTypeSelected && (
        <Typography sx={guaranteesClientPageStyles.flowLockHint}>
          {guaranteesClientText.securityTypeRequiredHint}
        </Typography>
      )}

      <Box sx={guaranteesClientPageStyles.flowActions}>
        <Button
          type="button"
          variant="contained"
          disabled={isSaveButtonDisabled}
          onClick={() => void onSave()}
          startIcon={isSavingAccount ? <ButtonLoadingIcon /> : undefined}
          sx={dataFormStyles.documentPrimaryAction}
        >
          {guaranteesClientText.saveButton}
        </Button>
      </Box>
    </FlowSection>
  );
};

export default AccountSection;
